import HebrewDefinitionLookup from '../definitions/HebrewDefinitionLookup'
import HebrewDefinitionTransformer from '../definitions/HebrewDefinitionTransformer'
import InterlinearRenderer from '../html/InterlinearRenderer'
import StyledRenderer from '../html/StyledRenderer'
import { InterlinearNumberStyle } from '../html/VersionStyle'
import VersionStyles from '../html/VersionStyles'
import MorphologyLookup from '../morphology/MorphologyLookup'
import MorphologyTransformer from '../morphology/MorphologyTransformer'
import SourceCode from '../sources/SourceCode'
import SourceStore from '../sources/SourceStore'
import SourceTestament from '../sources/SourceTestament'
import SourceType from '../sources/SourceType'
import Version from './Version'
import VersionBuilder from './VersionBuilder'
import VersionGroups from './VersionGroups'

export default class VersionFactory {

    private static readonly kitUnstyledWords = [
        'in',
        'to',
        'of',
        'the',
        'a',
        'and',
        'for',
        'my',
        'his',
        'they',
        'their',
        'your',
        'we',
        'she',
        'he',
        'hers',
        'yours',
        'theirs',
        'ours',
        'me',
        'or',
        'upon',
        'on',
        'into'
    ];

    public static getVersions(sources: SourceStore, hebrewDefinitions: HebrewDefinitionLookup,
            morphology: MorphologyLookup): Version[] {
        return [
            VersionBuilder.english(sources, SourceCode.KJV).build(),

            // KJVi
            Version.fromSource(sources.getSource(SourceCode.KJVi), {
                group: VersionGroups.interlinear,
                style: VersionStyles.default({
                    classes: ['_TightInterlinearNumbers'],
                    interlinearNumberStyle: InterlinearNumberStyle.ReplaceNonSequential
                })
            }),

            VersionBuilder.english(sources, SourceCode.ASV).build(),
            VersionBuilder.english(sources, SourceCode.JPS).build(),
            VersionBuilder.english(sources, SourceCode.NWT).build(),
            VersionBuilder.english(sources, SourceCode.GLT).build(),
            VersionBuilder.english(sources, SourceCode.YLT).build(),
            VersionBuilder.english(sources, SourceCode.MLV).build(),
            VersionBuilder.english(sources, SourceCode.THOM).build(),
            VersionBuilder.english(sources, SourceCode.LXXb).build(),

            VersionBuilder.create(VersionGroups.hebrewCodices, sources, SourceCode.BHSox).hebrew().allowLexiconLookup().addStandardSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.hebrewCodices, sources, SourceCode.WLC, SourceType.Version).hebrew().allowLexiconLookup().addStandardSequentialNumbers().build(),

            VersionBuilder.english(sources, SourceCode.TYN).build(),
            VersionBuilder.english(sources, SourceCode.PESM).build(),
            VersionBuilder.english(sources, SourceCode.ED).build(),

            // KIT
            Version.fromSource(sources.getSource(SourceCode.KIT), {
                group: VersionGroups.interlinear,
                style: VersionStyles.default({
                    classes: ['_TightInterlinearNumbers'],
                    interlinearNumberStyle: InterlinearNumberStyle.ReplaceSequential
                })
            }),

            // KIT Styled
            Version.create({
                code: 'KITc',
                group: VersionGroups.english,
                index: sources.getSource(SourceCode.KIT).getIndex() + 0.5,
                name: 'Styled Kingdom Interlinear Translation',
                renderer: new StyledRenderer(sources.getSource(SourceCode.KIT), [{
                    source: sources.getSource(SourceCode.WHO, SourceType.Parsing),
                    styleRules: [
                        { startsWith: 'n', styles: ['font-weight:bold'] },
                        { startsWith: 'nind', styles: ['color:purple', 'background-color:thistle'] },
                        { startsWith: 'ng', styles: ['color:peru'] },
                        { startsWith: 'ind', styles: ['font-weight:bold', 'color:teal', 'background-color:lightblue'] }
                    ]
                }, {
                    source: sources.getSource(SourceCode.WHOs),
                    sourceFormatter: text => text.replace(/[^a-zA-Z ]/g, ''), // Remove Greek accents.
                    styleRules: [
                        { equals: 'mesoj', styles: ['font-weight:bold', 'color:teal', 'background-color:lightblue'] },
                        { equals: 'kuklw', styles: ['font-weight:bold', 'color:teal', 'background-color:lightblue'] }
                    ]
                }], this.kitUnstyledWords),
                testament: SourceTestament.Old
            }),

            VersionBuilder.create(VersionGroups.hebrewCodices, sources, SourceCode.AlepMQ).hebrew().build(),
            VersionBuilder.create(VersionGroups.hebrewCodices, sources, SourceCode.AlepMM).hebrew().build(),
            VersionBuilder.create(VersionGroups.lxxCodices, sources, SourceCode.LXXr).greek().allowLexiconLookup().addStandardSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.lxxCodices, sources, SourceCode.LXXa).greek().build(),
            VersionBuilder.create(VersionGroups.hebrewCodices, sources, SourceCode.TransL).transliteration().addStandardSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.hebrewCodices, sources, SourceCode.TransS).transliteration().addStandardSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekCodices, sources, SourceCode.UBS4).greek().build(),
            VersionBuilder.create(VersionGroups.greekCodices, sources, SourceCode.TIS, SourceType.Version).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekCodices, sources, SourceCode.STE, SourceType.Version).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekCodices, sources, SourceCode.SCR, SourceType.Version).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekCodices, sources, SourceCode.BYZ, SourceType.Version).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekCodices, sources, SourceCode.NA27, SourceType.Version).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekCodices, sources, SourceCode.NA28).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekCodices, sources, SourceCode.TRE).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekCodices, sources, SourceCode.WHO, SourceType.Version).greek().allowLexiconLookup().addStandardSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekCodices, sources, SourceCode.Sinai).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekCodices, sources, SourceCode.SinaiO, SourceType.Version).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.correctedCodices, sources, SourceCode.Sinai3C, SourceType.Version).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekCodices, sources, SourceCode.AlexO, SourceType.Version).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.correctedCodices, sources, SourceCode.Alex2C, SourceType.Version).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekCodices, sources, SourceCode.VatB).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekCodices, sources, SourceCode.VatBO, SourceType.Version).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.correctedCodices, sources, SourceCode.VatB3C, SourceType.Version).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekCodices, sources, SourceCode.Rescr).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekCodices, sources, SourceCode.RescrO).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.correctedCodices, sources, SourceCode.Rescr3C).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekCodices, sources, SourceCode.Bezae).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.correctedCodices, sources, SourceCode.Bezae2C).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekCodices, sources, SourceCode.Claro).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.correctedCodices, sources, SourceCode.Claro4C).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekCodices, sources, SourceCode.Wash).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.correctedCodices, sources, SourceCode.Wash1C).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),

            // WLC Parsing
            Version.fromSource(sources.getSource(SourceCode.WLC, SourceType.Parsing), {
                group: VersionGroups.hebrewParsingAndStems,
                style: VersionStyles.parsing({
                    interlinearNumberStyle: InterlinearNumberStyle.AddStandardSequential,
                    wordPartSeparator: '+',
                    wordTooltipGenerator: new MorphologyTransformer(morphology)
                })
            }),
            // LXXrp Parsing
            Version.fromSource(sources.getSource(SourceCode.LXXrp, SourceType.Parsing), {
                group: VersionGroups.lxxParsingAndStems,
                style: VersionStyles.parsing({
                    interlinearNumberStyle: InterlinearNumberStyle.AddStandardSequential,
                    wordTooltipGenerator: new MorphologyTransformer(morphology)
                })
            }),
            // TIS Parsing
            Version.fromSource(sources.getSource(SourceCode.TIS, SourceType.Parsing), {
                group: VersionGroups.greekParsing,
                style: VersionStyles.parsing({
                    interlinearNumberStyle: InterlinearNumberStyle.AddAlternateSequential,
                    wordTooltipGenerator: new MorphologyTransformer(morphology)
                })
            }),
            // STE Parsing
            Version.fromSource(sources.getSource(SourceCode.STE, SourceType.Parsing), {
                group: VersionGroups.greekParsing,
                style: VersionStyles.parsing({
                    interlinearNumberStyle: InterlinearNumberStyle.AddAlternateSequential,
                    wordTooltipGenerator: new MorphologyTransformer(morphology)
                })
            }),
            // SCR Parsing
            Version.fromSource(sources.getSource(SourceCode.SCR, SourceType.Parsing), {
                group: VersionGroups.greekParsing,
                style: VersionStyles.parsing({
                    interlinearNumberStyle: InterlinearNumberStyle.AddAlternateSequential,
                    wordTooltipGenerator: new MorphologyTransformer(morphology)
                })
            }),
            // BYZ Parsing
            Version.fromSource(sources.getSource(SourceCode.BYZ, SourceType.Parsing), {
                group: VersionGroups.greekParsing,
                style: VersionStyles.parsing({
                    interlinearNumberStyle: InterlinearNumberStyle.AddAlternateSequential,
                    wordTooltipGenerator: new MorphologyTransformer(morphology)
                })
            }),
            // NA27 Parsing
            Version.fromSource(sources.getSource(SourceCode.NA27, SourceType.Parsing), {
                group: VersionGroups.greekParsing,
                style: VersionStyles.parsing({
                    interlinearNumberStyle: InterlinearNumberStyle.AddAlternateSequential,
                    wordTooltipGenerator: new MorphologyTransformer(morphology)
                })
            }),
            // WHO Parsing
            Version.fromSource(sources.getSource(SourceCode.WHO, SourceType.Parsing), {
                group: VersionGroups.greekParsing,
                style: VersionStyles.parsing({
                    interlinearNumberStyle: InterlinearNumberStyle.AddStandardSequential,
                    wordTooltipGenerator: new MorphologyTransformer(morphology)
                })
            }),
            // SinaiO Parsing
            Version.fromSource(sources.getSource(SourceCode.SinaiO, SourceType.Parsing), {
                group: VersionGroups.greekParsing,
                style: VersionStyles.parsing({
                    interlinearNumberStyle: InterlinearNumberStyle.AddAlternateSequential,
                    wordTooltipGenerator: new MorphologyTransformer(morphology)
                })
            }),
            // Sinai3C Parsing
            Version.fromSource(sources.getSource(SourceCode.Sinai3C, SourceType.Parsing), {
                group: VersionGroups.greekParsing,
                style: VersionStyles.parsing({
                    interlinearNumberStyle: InterlinearNumberStyle.AddAlternateSequential,
                    wordTooltipGenerator: new MorphologyTransformer(morphology)
                })
            }),
            // AlexO Parsing
            Version.fromSource(sources.getSource(SourceCode.AlexO, SourceType.Parsing), {
                group: VersionGroups.greekParsing,
                style: VersionStyles.parsing({
                    interlinearNumberStyle: InterlinearNumberStyle.AddAlternateSequential,
                    wordTooltipGenerator: new MorphologyTransformer(morphology)
                })
            }),
            // Alex2C Parsing
            Version.fromSource(sources.getSource(SourceCode.Alex2C, SourceType.Parsing), {
                group: VersionGroups.greekParsing,
                style: VersionStyles.parsing({
                    interlinearNumberStyle: InterlinearNumberStyle.AddAlternateSequential,
                    wordTooltipGenerator: new MorphologyTransformer(morphology)
                })
            }),
            // VatBO Parsing
            Version.fromSource(sources.getSource(SourceCode.VatBO, SourceType.Parsing), {
                group: VersionGroups.greekParsing,
                style: VersionStyles.parsing({
                    interlinearNumberStyle: InterlinearNumberStyle.AddAlternateSequential,
                    wordTooltipGenerator: new MorphologyTransformer(morphology)
                })
            }),
            // VatB3C Parsing
            Version.fromSource(sources.getSource(SourceCode.VatB3C, SourceType.Parsing), {
                group: VersionGroups.greekParsing,
                style: VersionStyles.parsing({
                    interlinearNumberStyle: InterlinearNumberStyle.AddAlternateSequential,
                    wordTooltipGenerator: new MorphologyTransformer(morphology)
                })
            }),

            // WLCs
            Version.fromSource(sources.getSource(SourceCode.WLCs), {
                group: VersionGroups.hebrewParsingAndStems,
                style: VersionStyles.hebrew({
                    allowLexiconLookup: true,
                    interlinearNumberStyle: InterlinearNumberStyle.AddStandardSequential,
                    wordTooltipGenerator: new HebrewDefinitionTransformer(hebrewDefinitions, false)
                })
            }),

            VersionBuilder.create(VersionGroups.lxxParsingAndStems, sources, SourceCode.LXXrs).greek().allowLexiconLookup().addStandardSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.hebrewParsingAndStems, sources, SourceCode.STRs).hebrew().allowLexiconLookup().build(),
            VersionBuilder.create(VersionGroups.hebrewParsingAndStems, sources, SourceCode.WSTRs).hebrew().allowLexiconLookup().addStandardSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekStems, sources, SourceCode.TISs).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekStems, sources, SourceCode.STEs).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekStems, sources, SourceCode.SCRs).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekStems, sources, SourceCode.BYZs).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekStems, sources, SourceCode.NA27s).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekStems, sources, SourceCode.WHOs).greek().allowLexiconLookup().addStandardSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekStems, sources, SourceCode.SinaiOs).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekStems, sources, SourceCode.Sinai3Cs).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekStems, sources, SourceCode.AlexOs).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekStems, sources, SourceCode.Alex2Cs).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekStems, sources, SourceCode.VatBOs).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),
            VersionBuilder.create(VersionGroups.greekStems, sources, SourceCode.VatB3Cs).greek().allowLexiconLookup().addAlternateSequentialNumbers().build(),

            VersionBuilder.international(sources, SourceCode.PAB).build(),
            VersionBuilder.international(sources, SourceCode.LUT).build(),
            VersionBuilder.international(sources, SourceCode.SCH).build(),
            VersionBuilder.international(sources, SourceCode.SRV).build(),
            VersionBuilder.international(sources, SourceCode.LU2).build(),
            VersionBuilder.international(sources, SourceCode.DBV).build(),
            VersionBuilder.international(sources, SourceCode.IRB).build(),
            VersionBuilder.international(sources, SourceCode.SVV).build(),
            VersionBuilder.international(sources, SourceCode.CRV).build(),
            VersionBuilder.international(sources, SourceCode.RUS).build(),
            VersionBuilder.international(sources, SourceCode.UKR).build(),

            // WLCi
            /*Version.create({
                code: 'WLCi',
                group: VersionGroups.interlinear,
                index: 1000,
                name: 'Hebrew Interlinear',
                renderer: new InterlinearRenderer()
                    .addLine({
                        source: sources.getSource(SourceCode.WLC, SourceType.Version),
                        style: VersionStyles.hebrew({
                            allowLexiconLookup: true,
                            interlinearNumberStyle: InterlinearNumberStyle.AddStandardSequential
                        })
                    })
                    .addLine({
                        source: sources.getSource(SourceCode.WLC, SourceType.Parsing),
                        style: VersionStyles.parsing({
                            wordPartSeparator: '+',
                            wordTooltipGenerator: new MorphologyTransformer(morphology)
                        })
                    })
                    .addLine({
                        source: sources.getSource(SourceCode.WSTRs),
                        style: VersionStyles.hebrew({
                            allowLexiconLookup: true,
                            wordTooltipGenerator: new HebrewDefinitionTransformer(hebrewDefinitions, false)
                        })
                    })
                    .addLine({
                        source: sources.getSource(SourceCode.WSTRs),
                        style: VersionStyles.default({
                            classes: ['_WrappableInterlinearLine'],
                            wordPartJoiner: ' + ',
                            wordPartSeparator: WordStyles.hebrewPartSeparator,
                            wordPartTransformer: new HebrewDefinitionTransformer(hebrewDefinitions, true)
                        })
                    }),
                testament: SourceTestament.Old
            }),*/

            // WLCi
            Version.create({
                code: 'WLCi',
                group: VersionGroups.interlinear,
                index: 1001,
                name: 'Hebrew Interlinear',
                renderer: new InterlinearRenderer()
                    .addLine({
                        source: sources.getSource(SourceCode.WLC, SourceType.Version),
                        style: VersionStyles.hebrew({
                            allowLexiconLookup: true,
                            interlinearNumberStyle: InterlinearNumberStyle.AddStandardSequential
                        })
                    })
                    .addLine({
                        source: sources.getSource(SourceCode.WLC, SourceType.Parsing),
                        style: VersionStyles.parsing({
                            wordPartSeparator: '+',
                            wordTooltipGenerator: new MorphologyTransformer(morphology)
                        })
                    })
                    .addLine({
                        source: sources.getSource(SourceCode.WSTRs),
                        style: VersionStyles.hebrew({
                            allowLexiconLookup: true,
                            wordTooltipGenerator: new HebrewDefinitionTransformer(hebrewDefinitions, false)
                        })
                    })
                    .addLine({
                        source: sources.getSource(SourceCode.WLCe),
                        style: VersionStyles.default({
                            classes: ['_WrappableInterlinearLine']
                        }),
                        wordSeparator: '%'
                    }),
                testament: SourceTestament.Old
            }),

            // WHOi
            Version.create({
                code: 'WHOi',
                group: VersionGroups.interlinear,
                index: 1002,
                name: 'Greek Interlinear',
                renderer: new InterlinearRenderer()
                    .addLine({
                        source: sources.getSource(SourceCode.WHO, SourceType.Version),
                        style: VersionStyles.greek({
                            allowLexiconLookup: true,
                            interlinearNumberStyle: InterlinearNumberStyle.AddStandardSequential
                        })
                    })
                    .addLine({
                        source: sources.getSource(SourceCode.WHO, SourceType.Parsing),
                        style: VersionStyles.parsing({
                            wordTooltipGenerator: new MorphologyTransformer(morphology)
                        })
                    })
                    .addLine({
                        source: sources.getSource(SourceCode.WHOs),
                        style: VersionStyles.greek({
                            allowLexiconLookup: true
                        })
                    })
                    .addLine({
                        source: sources.getSource(SourceCode.KIT),
                        style: VersionStyles.default({
                            classes: ['_WrappableInterlinearLine']
                        }),
                        wordSeparator: '%'
                    }),
                testament: SourceTestament.New
            }),

            // LXXi
            Version.create({
                code: 'LXXi',
                group: VersionGroups.interlinear,
                index: 1003,
                name: 'LXX Rahlf Interlinear (LXT-BLM from BibleWorks 10)',
                renderer: new InterlinearRenderer()
                    .addLine({
                        source: sources.getSource(SourceCode.LXXr),
                        style: VersionStyles.greek({
                            allowLexiconLookup: true,
                            interlinearNumberStyle: InterlinearNumberStyle.AddStandardSequential
                        })
                    })
                    .addLine({
                        source: sources.getSource(SourceCode.LXXrp),
                        style: VersionStyles.parsing({
                            wordTooltipGenerator: new MorphologyTransformer(morphology)
                        })
                    })
                    .addLine({
                        source: sources.getSource(SourceCode.LXXrs),
                        style: VersionStyles.greek({
                            allowLexiconLookup: true
                        })
                    })
                    .addLine({
                        source: sources.getSource(SourceCode.LXXe),
                        style: VersionStyles.default({
                            classes: ['_WrappableInterlinearLine']
                        }),
                        wordSeparator: '%'
                    }),
                testament: SourceTestament.New
            })
        ];
    }
}
